import {
  ApplicationConfig,
  EnvironmentProviders,
  Provider,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter, RouteReuseStrategy, TitleStrategy, withInMemoryScrolling } from '@angular/router';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideGoogleAnalytics } from '@hakimio/ngx-google-analytics';
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_PAGINATOR_DEFAULT_OPTIONS, MatPaginatorIntl } from '@angular/material/paginator';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { TitleService } from 'portal/core/title/title.service';
import { PaginatorIntlService } from 'portal/core/paginator/paginator-intl.service';
import { CoreRouteReuseStrategy } from 'portal/core/core.route-reuse-strategy';
import { initializeTranslations } from 'portal/core/language/translation.initializer';
import { authInterceptor } from 'portal/core/auth/auth.interceptor';
import { initializePlanSync } from 'portal/core/plan-sync/plan-sync.initializer';
import { isPreviewDocument } from 'portal/core/preview/preview-document';

/**
 * Providers that talk to the outside world on bootstrap: plan sync (may call `/api` when a token is
 * stored) and Google Analytics (`gtag('config')` sends a `page_view` by itself). The admin preview
 * document (`/preview/*`) starts neither.
 */
export const sessionEffectsProviders = (preview: boolean): (Provider | EnvironmentProviders)[] =>
  preview
    ? []
    : [provideAppInitializer(initializePlanSync), provideGoogleAnalytics(environment.gaTrackingId)];

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideRouter(
      routes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'top',
      }),
    ),
    {
      provide: RouteReuseStrategy,
      useClass: CoreRouteReuseStrategy,
    },
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: './i18n/',
        suffix: `.json?v=${environment.version}`
      }),
      fallbackLang: 'uk',
    }),
    provideAppInitializer(initializeTranslations),
    ...sessionEffectsProviders(isPreviewDocument()),
    {
      provide: MAT_ICON_DEFAULT_OPTIONS,
      useValue: { fontSet: 'material-symbols-outlined' },
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: { duration: environment.snackbar?.duration ?? 2500 },
    },
    {
      provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
      useValue: { showFirstLastButtons: true }
    },
    {
      provide: MatPaginatorIntl,
      useClass: PaginatorIntlService
    },
    {
      provide: TitleStrategy,
      useClass: TitleService,
    },
  ],
};
